
import { ROOT_ACTIONS } from "@/store/actions";
import { Component, Vue } from "vue-property-decorator";
import { firestore } from "@/firebase";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { Activity, ActualActivity } from "@/types";
import { toActivityDate } from "@/utils/activityDate";
import { format } from "date-fns";
import { nl, fr } from "date-fns/locale";

@Component
export default class ConfirmActivity extends Vue {
  step = 1;
  pageLoading = true;
  error = "";
  loading = false;

  activity = null as Activity | null;
  actualActivity = null as ActualActivity | null;

  get authenticated() {
    return this.$store.state.user.authenticated;
  }

  async mounted() {
    const browserLang = window.navigator.language;
    if (!this.authenticated) {
      this.$i18n.locale = browserLang.startsWith("fr") ? "fr" : "nl";
    }
    await this.fetchActivity();
  }

  async fetchActivity() {
    this.pageLoading = true;
    this.error = "";
    this.$store.dispatch(ROOT_ACTIONS.SET_BREADCRUMBS, []);
    const activityId = this.$route.query.activity as string;
    if (!activityId) {
      this.error = String(this.$t("confirmActivityMissingId"));
      this.pageLoading = false;
      return;
    }

    try {
      const actualActivity = await getDoc(
        doc(firestore, "actual-activities", activityId)
      );
      if (!actualActivity.exists()) {
        this.error = String(this.$t("confirmActivityNotFound"));
        return;
      }

      this.actualActivity = {
        id: actualActivity.id,
        ...actualActivity.data(),
      } as ActualActivity;

      const activity = await getDoc(
        doc(firestore, "activities", actualActivity.data()!.activityId)
      );
      if (!activity.exists()) {
        this.error = String(this.$t("confirmActivityNotFound"));
        return;
      }

      this.activity = {
        id: activity.id,
        ...activity.data(),
      } as Activity;
    } catch (e) {
      this.error = String(this.$t("confirmActivityLoadError"));
    } finally {
      this.pageLoading = false;
    }
  }

  formatDate(date: ActualActivity["date"] | Date | { seconds?: number } | undefined) {
    const parsed = toActivityDate(date);
    if (!parsed) return "";

    return format(parsed, "EEEE dd/MM/yyyy - HH:mm", {
      locale: this.$i18n.locale === "fr" ? fr : nl,
    });
  }

  formatHours(hours: number) {
    const rhours = Math.floor(hours);
    const minutes = hours % 1;
    if (minutes === 0) return `${rhours}h`;

    const rminutes = Math.ceil(minutes * 60);
    return `${rhours}h${rminutes}m`;
  }

  async confirmActivity() {
    if (!this.actualActivity) {
      return;
    }

    this.loading = true;
    try {
      await updateDoc(
        doc(firestore, "actual-activities", this.actualActivity.id!),
        {
          participantConfirmed: true,
        }
      );

      await this.fetchActivity();
    } catch (e) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: this.$t("confirmActivityConfirmError"),
      });
    } finally {
      this.loading = false;
    }
  }
}
